import React, {
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import {
  Anchor,
  ConfigProvider,
  Tabs,
} from '@bedrock/components';
import classNames from 'classnames';
import { useDebounceFn, useThrottleFn, useFullscreen, useMemoizedFn, useUpdate } from 'ahooks';
import './index.less';
import { isEqual } from 'lodash'

const cx = classNames;

const Link = Anchor.Link;

let tabAnchorId = 0;

//
type TabsAnchorProps = {
  children: React.ReactNode;
  // 推导出Tabs的属性
  tabsProps?: Omit<React.ComponentProps<typeof Tabs>, 'activeKey'>; // tabs的属性
  contentWrapperProps?: React.HTMLAttributes<HTMLDivElement>; // 内容区域的属性
  getAnchorHeader?: (wrapperDom: Element) => Element[]; // 获取锚点头部的方法
  renderHeaderTab?: (item: Element, index: number) => React.ReactElement; // tabPane的渲染方法
  offsetTop?: number; // 锚点距离顶部的距离
} & React.HTMLAttributes<HTMLDivElement>;

const TabsAnchor = (
  props: TabsAnchorProps,
  ref
) => {
  const {children,
    getAnchorHeader = (wrapperDom) => Array.from(wrapperDom.querySelectorAll('[data-is-anchor]')),
    tabsProps,
    contentWrapperProps,
    className,
    ...resProps
  } = props;
  const idRef = useRef<number>(++tabAnchorId);
  const wrapperRef = React.useRef<HTMLDivElement>();
  const anchorRef = React.useRef<any>();
  const [selectedId, setSelectedId] = useState<string>(`#tab-${idRef.current}-0`);
  const [headerList, setHeaderList] = useState<Element[]>([]);
  const update = useUpdate();

  const { run: setHeaderListDebounce, cancel: cancelSetHeaderList } = useThrottleFn(() => {
    if (!wrapperRef.current) return;
    const newHeaderList = getAnchorHeader(wrapperRef.current).map((item: any, index) => {
      item.id = `tab-${idRef.current}-${index}`;
      return item;
    });
    if (newHeaderList.length && !isEqual(newHeaderList, headerList)) {
      setHeaderList(newHeaderList);
    }
    update()
  }, {
    wait: 500,
  });

  useImperativeHandle(ref, () => ({
    setActiveTab: (activeKey: string) => {
      setSelectedId(activeKey);
      anchorRef.current?.setActiveAnchor?.(activeKey, true);
    },
  }), []);


  useEffect(() => {
    setHeaderListDebounce();
    let observer: MutationObserver;
    if (wrapperRef?.current) {
      // 监听wrapperRef的dom变化，获取所有 is-anchor-header
      observer = new MutationObserver((mutations) => {
        if (!mutations.length) return;
        setHeaderListDebounce();
      });
      observer.observe(wrapperRef.current, {
        childList: true,
        subtree: true,
      });
    }
    return () => {
      cancelSetHeaderList?.();
      observer?.disconnect?.();
    }
  }, [])
  return (
    <ConfigProvider getPopupContainer={() => wrapperRef?.current}>
      <div {...resProps} className={cx('mpc-tabs-Anchor', className)}>
        <Tabs
          {...tabsProps}
          className={cx('mpc-tabs-Anchor-tabs', tabsProps?.className)}
          onChange={(value: string) => {
            tabsProps?.onChange?.(value);
            setSelectedId(value);
            anchorRef.current?.setActiveAnchor?.(value, true);
          }}
          activeKey={selectedId}
        >
          {headerList.map((item, index) => {
            return <Tabs.TabPane tab={props.renderHeaderTab?.(item, index) || item.textContent} key={`#tab-${idRef.current}-${index}`} />
          })}
          {!headerList?.length && <Tabs.TabPane tab={' '} key={`loading`} />}
        </Tabs>

        <div {...contentWrapperProps} ref={wrapperRef} className={cx('mpc-anchor-content-wrapper', contentWrapperProps?.className)}>
          {children}
        </div>
      </div>
      <Anchor
          // 能扔多远扔多远
        offsetTop={10}
        style={{ display: 'none' }}
        useCaret={false}
        ref={anchorRef}
        onChange={(currentActiveLink: string) => {
          setSelectedId(currentActiveLink);
        }}
      >
        {headerList.map((item, index) => {
          return (
            <Link
              id={'#anchor' + `tab-${idRef.current}-${index}`}
              href={`#tab-${idRef.current}-${index}`}
            />
          );
        })}
      </Anchor>
    </ConfigProvider>
  );
};

export default forwardRef(TabsAnchor);
